import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Edit,
  Archive,
  Users,
  Calendar,
  Settings,
  UserPlus,
  Search,
  X,
  Wind,
  Printer,
  Megaphone,
  Globe,
  Server,
  Bot,
  Wrench,
  Calculator,
  GraduationCap,
  Mail,
  Code,
  ShoppingBag,
  Briefcase,
  Building2,
  Layout,
  Cpu,
} from "lucide-react";
import { useProject } from "../contexts/ProjectContext";
import { useUser } from "../contexts/UserContext";
import { useNotification } from "../contexts/NotificationContext";
import { useSocket } from "../contexts/SocketContext";
import CreateProjectModal from "./CreateProjectModal";
import EditProjectModal from "./EditProjectModal";
import AssignUserModal from "./AssignUserModal";
import InviteUserModal from "./InviteUserModal";
import Avatar from "./Avatar";
import { RunningTasksList } from "./TimeTracking";
import { stripHtmlTags } from "../utils/htmlUtils";
import ConfirmationModal from "./ConfirmationModal";
import { timeEntryAPI } from "../utils/api";
import {
  getProjectStatusColors,
  getProjectTypeColors,
  getStatusBadgeClasses,
} from "../utils/statusColors";

// Industry-specific icon and theme helper
const getProjectIndustryIcon = (project) => {
  const text = `${project?.name || ""} ${project?.clientName || ""} ${project?.category || ""} ${project?.description || ""}`.toLowerCase();

  if (text.includes("air") || text.includes("ac ") || text.includes("cooling") || text.includes("hvac") || text.includes("cool")) {
    return { icon: Wind, bg: "bg-cyan-100 dark:bg-cyan-900/30", color: "text-cyan-600 dark:text-cyan-400" };
  }
  if (text.includes("print")) {
    return { icon: Printer, bg: "bg-indigo-100 dark:bg-indigo-900/30", color: "text-indigo-600 dark:text-indigo-400" };
  }
  if (text.includes("market") || text.includes("seo") || text.includes("campaign") || text.includes("social")) {
    return { icon: Megaphone, bg: "bg-purple-100 dark:bg-purple-900/30", color: "text-purple-600 dark:text-purple-400" };
  }
  if (text.includes("web") || text.includes("site") || text.includes(".at") || text.includes(".com") || text.includes("portal") || text.includes("online")) {
    return { icon: Globe, bg: "bg-blue-100 dark:bg-blue-900/30", color: "text-blue-600 dark:text-blue-400" };
  }
  if (text.includes("erp") || text.includes("server") || text.includes("database") || text.includes("middleware") || text.includes("system")) {
    return { icon: Server, bg: "bg-slate-200 dark:bg-slate-700/60", color: "text-slate-700 dark:text-slate-300" };
  }
  if (text.includes("ai") || text.includes("bot") || text.includes("intelligence") || text.includes("smart") || text.includes("gpt")) {
    return { icon: Bot, bg: "bg-violet-100 dark:bg-violet-900/30", color: "text-violet-600 dark:text-violet-400" };
  }
  if (text.includes("repair") || text.includes("technik") || text.includes("tool") || text.includes("fix") || text.includes("maint")) {
    return { icon: Wrench, bg: "bg-amber-100 dark:bg-amber-900/30", color: "text-amber-600 dark:text-amber-400" };
  }
  if (text.includes("account") || text.includes("finance") || text.includes("datiiv") || text.includes("tax") || text.includes("book")) {
    return { icon: Calculator, bg: "bg-emerald-100 dark:bg-emerald-900/30", color: "text-emerald-600 dark:text-emerald-400" };
  }
  if (text.includes("lms") || text.includes("learn") || text.includes("school") || text.includes("course") || text.includes("edu")) {
    return { icon: GraduationCap, bg: "bg-teal-100 dark:bg-teal-900/30", color: "text-teal-600 dark:text-teal-400" };
  }
  if (text.includes("mail") || text.includes("email") || text.includes("newsletter") || text.includes("msg")) {
    return { icon: Mail, bg: "bg-rose-100 dark:bg-rose-900/30", color: "text-rose-600 dark:text-rose-400" };
  }
  if (text.includes("code") || text.includes("dev") || text.includes("markup") || text.includes("app")) {
    return { icon: Code, bg: "bg-orange-100 dark:bg-orange-900/30", color: "text-orange-600 dark:text-orange-400" };
  }
  if (text.includes("shop") || text.includes("store") || text.includes("cart") || text.includes("ecom")) {
    return { icon: ShoppingBag, bg: "bg-pink-100 dark:bg-pink-900/30", color: "text-pink-600 dark:text-pink-400" };
  }

  const fallbackIcons = [
    { icon: Briefcase, bg: "bg-blue-100 dark:bg-blue-900/30", color: "text-blue-600 dark:text-blue-400" },
    { icon: Building2, bg: "bg-indigo-100 dark:bg-indigo-900/30", color: "text-indigo-600 dark:text-indigo-400" },
    { icon: Layout, bg: "bg-emerald-100 dark:bg-emerald-900/30", color: "text-emerald-600 dark:text-emerald-400" },
    { icon: Cpu, bg: "bg-purple-100 dark:bg-purple-900/30", color: "text-purple-600 dark:text-purple-400" },
  ];
  const charCode = (project?._id || project?.name || "a").charCodeAt(0) || 0;
  return fallbackIcons[charCode % fallbackIcons.length];
};

const AdminPanel = () => {
  const { projects, loading, deleteProject, fetchProjects } = useProject();
  const { users } = useUser();
  const { showToast } = useNotification();
  // const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isArchiving, setIsArchiving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Running tasks state
  // const [runningTasks, setRunningTasks] = useState([]);
  // const [loadingRunningTasks, setLoadingRunningTasks] = useState(false);
  // const { socket } = useSocket();

  // Fetch all running tasks (admin view)
  // const fetchRunningTasks = async () => {
  //   try {
  //     setLoadingRunningTasks(true);
  //     const response = await timeEntryAPI.getRunningTasks();
  //     if (response.data?.success) {
  //       setRunningTasks(response.data.runningTasks || []);
  //     }
  //   } catch (error) {
  //     console.error("Failed to fetch running tasks:", error);
  //   } finally {
  //     setLoadingRunningTasks(false);
  //   }
  // };

  // Load running tasks on mount
  // useEffect(() => {
  //   fetchRunningTasks();
  // }, []);

  // WebSocket listeners for real-time updates
  // useEffect(() => {
  //   if (!socket) return;

  //   const handleGlobalTimerStarted = (data) => {
  //     // Refetch running tasks when a timer is started
  //     fetchRunningTasks();
  //   };

  //   const handleGlobalTimerStopped = (data) => {
  //     // Remove the task from running tasks when timer stops
  //     setRunningTasks((prev) =>
  //       prev.filter((task) => task.taskId !== data.cardId)
  //     );
  //   };

  //   const handleGlobalTimerDiscarded = (data) => {
  //     // Remove the task from running tasks when timer is discarded
  //     setRunningTasks((prev) =>
  //       prev.filter((task) => task.taskId !== data.cardId)
  //     );
  //   };

  //   // Only listen to global events to avoid duplicate operations
  //   socket.on("global-timer-started", handleGlobalTimerStarted);
  //   socket.on("global-timer-stopped", handleGlobalTimerStopped);
  //   socket.on("global-timer-discarded", handleGlobalTimerDiscarded);

  //   return () => {
  //     socket.off("global-timer-started", handleGlobalTimerStarted);
  //     socket.off("global-timer-stopped", handleGlobalTimerStopped);
  //     socket.off("global-timer-discarded", handleGlobalTimerDiscarded);
  //   };
  // }, [socket]);

  useEffect(() => {
    // ensure projects list is loaded
    if (!projects || projects.length === 0) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleArchiveProject = (project) => {
    setProjectToArchive(project);
    setShowArchiveConfirm(true);
  };

  const confirmArchiveProject = async () => {
    if (!projectToArchive) return;

    try {
      setIsArchiving(true);
      await deleteProject(projectToArchive._id);
      showToast("Project archived successfully. You can restore it from the Archived Projects section.", "success");
      setShowArchiveConfirm(false);
      setProjectToArchive(null);
      await fetchProjects();
    } catch (error) {
      showToast("Failed to archive project", "error");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleAssignUsers = (project) => {
    setSelectedProject(project);
    setShowAssignModal(true);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const handleInviteUsers = (project) => {
    setSelectedProject(project);
    setShowInviteModal(true);
  };

  const handleProjectUpdated = async () => {
    // Refresh projects when users are added/removed
    console.log("🔄 Refreshing projects after user change...");
    await fetchProjects();
    setRefreshKey((prev) => prev + 1); // Force re-render
    console.log("✅ Projects refreshed");
  };

  // Filter projects by search query (name, client, description, id, members)
  const filteredProjects = (projects || []).filter((project) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const parts = [
      project.name || "",
      project.clientName || "",
      stripHtmlTags(project.description) || "",
      project._id || "",
    ];
    // include member names/emails if available
    if (Array.isArray(project.members)) {
      project.members.forEach((m) => {
        if (m && m.user) {
          parts.push(m.user.name || m.user.email || "");
        }
      });
    }
    const hay = parts.join(" ").toLowerCase();
    return hay.includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-lg px-6 h-[72px] min-h-[72px] flex items-center shadow-sm border border-gray-200 dark:border-slate-700 transition-colors duration-200 box-border">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-3">
            <Link
              to="/"
              className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>

            <div>
              <h1 className="text-base font-semibold leading-tight mb-0.5 dark:text-white">Admin Panel</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400 leading-tight">
                Manage projects and team members
              </p>
            </div> 
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex items-center gap-2">
              <div className="text-xs font-semibold w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-700 dark:text-blue-400">
                {projects?.length || 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-slate-400 font-medium">Projects</div>
            </div>
          </div>
        </div>
      </div>

      {/* Running Tasks Section */}
      {/* {runningTasks.length > 0 && (
        <RunningTasksList
          tasks={runningTasks}
          title="Running Tasks - Team"
          onTaskClick={(task) => {
            navigate(`/project/${task.projectId}/card/${task.taskId}`);
          }}
        />
      )} */}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg px-6 h-[72px] min-h-[72px] flex items-center shadow-sm border border-gray-200 dark:border-slate-700 transition-colors duration-200 box-border">
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-slate-400">Total Projects</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{projects?.length || 0}</p>
            </div>
            <div className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Settings className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg px-6 h-[72px] min-h-[72px] flex items-center shadow-sm border border-gray-200 dark:border-slate-700 transition-colors duration-200 box-border">
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-slate-400">Team Members</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{users.length}</p>
            </div>
            <div className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg px-6 h-[72px] min-h-[72px] flex items-center shadow-sm border border-gray-200 dark:border-slate-700 transition-colors duration-200 box-border">
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-slate-400">Active Projects</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{projects?.filter((p) => p?.status === "active").length || 0}</p>
            </div>
            <div className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Projects Management */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 transition-colors duration-200">
        <div className="px-6 h-[72px] min-h-[72px] flex items-center border-b border-gray-200 dark:border-slate-700 box-border sticky -top-[0.99rem] z-10 bg-white dark:bg-slate-800 rounded-t-lg">
          <div className="flex items-center justify-between w-full flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">Projects</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 leading-tight">Manage your projects and team assignments</p>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects by name, client, description..."
                  className="w-full h-8 min-h-[32px] max-h-[32px] pl-9 pr-8 text-xs bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-black dark:text-white transition-colors box-border"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap">{filteredProjects.length} results</div>

              <button
                onClick={() => setShowCreateModal(true)}
                className="h-8 min-h-[32px] max-h-[32px] bg-blue-600 text-white hover:bg-blue-700 font-medium px-3.5 rounded-lg transition-colors duration-200 flex items-center space-x-1.5 text-xs whitespace-nowrap box-border"
              >
                <Plus className="w-4 h-4" />
                <span>Create Project</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-3">
          {!projects || projects.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                <Settings className="w-8 h-8 text-gray-400 dark:text-slate-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No projects yet</h3>
              <p className="text-gray-500 dark:text-slate-400 mb-6">Create your first project to get started</p>
              <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white hover:bg-blue-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2 mx-auto">
                <Plus className="w-4 h-4" />
                <span>Create Project</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4" key={refreshKey}>
              {filteredProjects.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                    <Settings className="w-8 h-8 text-gray-400 dark:text-slate-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No projects found</h3>
                  <p className="text-gray-500 dark:text-slate-400 mb-6">{searchQuery ? "Try a different search term" : "No projects yet"}</p>
                </div>
              ) : (
                filteredProjects.map((project) => {
                  // Safety check for project object
                  if (!project || !project._id) return null;
                  const industry = getProjectIndustryIcon(project);
                  const IndustryIcon = industry.icon;
                  return (
                    <div key={project._id} className="flex items-center justify-between px-4 h-[72px] min-h-[72px] max-h-[72px] bg-gray-50 dark:bg-slate-700/30 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors duration-200 border border-transparent dark:border-slate-700/50 box-border overflow-hidden">
                      <div className="flex items-center space-x-3.5 min-w-0 flex-1 mr-4">
                        <div className={`w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] ${industry.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <IndustryIcon className={`w-4 h-4 ${industry.color}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2.5 mb-1">
                            <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate">{project.name || "Unnamed Project"}</h3>
                            <div className="flex items-center space-x-1.5 flex-shrink-0">
                              <span className={getStatusBadgeClasses("projectStatus", project.projectStatus)}>
                                {getProjectStatusColors(project.projectStatus).label}
                              </span>
                              <span className={getStatusBadgeClasses("projectType", project.projectType)}>
                                {getProjectTypeColors(project.projectType).label}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2.5 text-xs text-gray-500 dark:text-slate-400">
                            <span className="truncate max-w-xs xl:max-w-md text-gray-600 dark:text-slate-400">
                              {stripHtmlTags(project.description)?.length > 100
                                ? stripHtmlTags(project.description).substring(0, 100) + "..."
                                : stripHtmlTags(project.description) || "No description"}
                            </span>
                            <span className="text-gray-400 dark:text-slate-500">•</span>
                            <div className="flex items-center space-x-1.5 flex-shrink-0">
                              <div className="flex -space-x-1">
                                {project.members?.filter((member) => member && member.user)?.slice(0, 3).map((member, index) => (
                                  <div key={member.user?._id || member.user || index} className="border-2 text-white border-white dark:border-slate-800 shadow-sm rounded-full">
                                    <Avatar user={member.user} size="xs" showTooltip={true} />
                                  </div>
                                ))}
                                {project.members?.filter((member) => member && member.user)?.length > 3 && (
                                  <div className="w-5 h-5 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center text-[10px] text-white dark:text-slate-200 font-medium shadow-sm bg-slate-400 dark:bg-slate-600">+{project.members.filter((member) => member && member.user).length - 3}</div>
                                )}
                              </div>
                              <span className="text-[11px] text-gray-500 dark:text-slate-400">{project.members?.filter((member) => member && member.user)?.length || 0} members</span>
                            </div>
                            <span className="text-gray-400 dark:text-slate-500">•</span>
                            <span className="text-[11px] text-gray-500 dark:text-slate-400 flex-shrink-0">Created {new Date(project.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <Link to={`/project/${project._id}`} className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200" title="View Project">
                          <Calendar className="w-4 h-4" />
                        </Link>
                        <button onClick={() => handleEditProject(project)} className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors duration-200" title="Edit Project">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleInviteUsers(project)} className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors duration-200" title="Invite Users">
                          <UserPlus className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleAssignUsers(project)} className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors duration-200" title="Manage Members">
                          <Users className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleArchiveProject(project)} className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200" title="Archive Project">
                          <Archive className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            // Only refresh projects after successful creation
            console.log("🔄 AdminPanel: Refreshing projects after project creation");
            fetchProjects();
          }}
        />
      )}

      {showEditModal && selectedProject && (
        <EditProjectModal
          project={selectedProject}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProject(null);
          }}
        />
      )}

      {showAssignModal && selectedProject && (
        <AssignUserModal
          project={selectedProject}
          onClose={() => setShowAssignModal(false)}
          onUserAssigned={() => {
            showToast("User assigned successfully!", "success");
          }}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {showInviteModal && selectedProject && (
        <InviteUserModal
          project={selectedProject}
          onClose={() => {
            setShowInviteModal(false);
            setSelectedProject(null);
          }}
          onUserInvited={async () => {
            await handleProjectUpdated(); // Refresh projects list
            showToast("User invited successfully!", "success");
          }}
        />
      )}

      {/* Archive Project Confirmation Modal */}
      <ConfirmationModal
        isOpen={showArchiveConfirm}
        onClose={() => {
          setShowArchiveConfirm(false);
          setProjectToArchive(null);
        }}
        onConfirm={confirmArchiveProject}
        title="Archive Project"
        message={
          projectToArchive
            ? `Are you sure you want to archive "${projectToArchive.name}"? The project will be moved to the Archived Projects section where you can restore it or permanently delete it later.`
            : "Are you sure you want to archive this project?"
        }
        confirmText="Archive Project"
        cancelText="Cancel"
        type="warning"
        isLoading={isArchiving}
      />
    </div>
  );
};

export default AdminPanel;
